// ═══════════════════════════════════════════════════════════════════════════
// PanneauFonctions.jsx
// Panneau Fonctions & Hooks — PesacoreIH
// Onglets : Routes · Jobs · Webhooks · Testeur
// ═══════════════════════════════════════════════════════════════════════════

import {
  ChevronDown,
  Clock,
  Globe,
  Layers,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Search,
  Send,
  Terminal,
  Trash2,
  Webhook,
  X
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../config/axiosConfig';
import usePesacoreIH from '../../stores/usePesacore';

// ─── Utilitaires UI ───────────────────────────────────────────────────────

function Badge({ children, color = 'zinc', size = 'sm' }) {
  const colors = {
    zinc:    'bg-zinc-800 text-zinc-400 border-zinc-700',
    sky:     'bg-sky-950/40 text-sky-300 border-sky-800/40',
    emerald: 'bg-emerald-950/40 text-emerald-300 border-emerald-800/40',
    amber:   'bg-amber-950/40 text-amber-300 border-amber-800/40',
    rose:    'bg-rose-950/40 text-rose-300 border-rose-800/40',
    purple:  'bg-purple-950/40 text-purple-300 border-purple-800/40',
    orange:  'bg-orange-950/40 text-orange-300 border-orange-800/40',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border font-mono ${size === 'xs' ? 'text-[9px]' : 'text-[10px]'} ${colors[color]}`}>
      {children}
    </span>
  );
}

function MethodBadge({ method }) {
  const map = { GET: 'emerald', POST: 'sky', PUT: 'amber', PATCH: 'amber', DELETE: 'rose', HEAD: 'zinc', OPTIONS: 'zinc' };
  return <Badge color={map[method] || 'zinc'}>{method}</Badge>;
}

function StatusBadge({ status }) {
  if (!status) return <Badge color="zinc">—</Badge>;
  const color = status >= 500 ? 'rose' : status >= 400 ? 'amber' : status >= 300 ? 'sky' : 'emerald';
  return <Badge color={color}>{status}</Badge>;
}

function Feedback({ msg, type, onClear }) {
  if (!msg) return null;
  const styles = {
    success:  'bg-emerald-950/30 border-emerald-800/30 text-emerald-300',
    error:    'bg-red-950/30 border-red-800/30 text-red-300',
    'dry-run':'bg-amber-950/30 border-amber-800/30 text-amber-300',
    info:     'bg-sky-950/30 border-sky-800/30 text-sky-300',
  };
  return (
    <div className={`flex items-center justify-between px-4 py-2.5 rounded border text-xs ${styles[type] || styles.info}`}>
      <span>{type === 'dry-run' && '🧪 DRY-RUN — '}{msg}</span>
      {onClear && <button onClick={onClear}><X size={12} /></button>}
    </div>
  );
}

// ─── Hook données ─────────────────────────────────────────────────────────

function useFunctionsData() {
  const { prepareAction, executeAction, dryRunMode } = usePesacoreIH();

  const [routes,   setRoutes]   = useState([]);
  const [jobs,     setJobs]     = useState([]);
  const [jobHistory, setJobHistory] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [whHistory, setWhHistory] = useState([]);
  const [middlewares, setMiddlewares] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  const fetchRoutes = useCallback(async () => {
    try {
      const r = await api.get('/superadmin/functions/routes');
      setRoutes(r.data.routes || []);
      setMiddlewares(r.data.middlewares || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const r = await api.get('/superadmin/functions/jobs');
      setJobs(r.data.jobs || []);
      setJobHistory(r.data.history || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, []);

  const fetchWebhooks = useCallback(async () => {
    try {
      const r = await api.get('/superadmin/functions/webhooks');
      setWebhooks(r.data.webhooks || []);
      setWhHistory(r.data.history || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchRoutes(), fetchJobs(), fetchWebhooks()]);
    setLoading(false);
  }, [fetchRoutes, fetchJobs, fetchWebhooks]);

  // Jobs actions
  const createJob = async (data) => {
    const prep = prepareAction('CREATE_JOB', data);
    if (!prep.valid) return { success: false, errors: prep.errors };
    if (prep.dryRun) return { success: true, dryRun: true };
    return executeAction(prep.commandId, async () => {
      const r = await api.post('/superadmin/functions/jobs', data);
      await fetchJobs();
      return r.data;
    });
  };

  const toggleJob = async (jobId) => {
    const r = await api.patch(`/superadmin/functions/jobs/${jobId}/toggle`);
    await fetchJobs();
    return r.data;
  };

  const deleteJob = async (jobId) => {
    const prep = prepareAction('DELETE_JOB', { jobId });
    if (!prep.valid) return { success: false, errors: prep.errors };
    if (prep.dryRun) return { success: true, dryRun: true };
    return executeAction(prep.commandId, async () => {
      const r = await api.delete(`/superadmin/functions/jobs/${jobId}`);
      await fetchJobs();
      return r.data;
    });
  };

  const runJobNow = async (jobId) => {
    const r = await api.post(`/superadmin/functions/jobs/${jobId}/run`);
    setTimeout(fetchJobs, 2000);
    return r.data;
  };

  // Webhooks actions
  const createWebhook = async (data) => {
    const prep = prepareAction('CREATE_WEBHOOK', data);
    if (!prep.valid) return { success: false, errors: prep.errors };
    if (prep.dryRun) return { success: true, dryRun: true };
    return executeAction(prep.commandId, async () => {
      const r = await api.post('/superadmin/functions/webhooks', data);
      await fetchWebhooks();
      return r.data;
    });
  };

  const deleteWebhook = async (webhookId) => {
    const prep = prepareAction('DELETE_WEBHOOK', { webhookId });
    if (!prep.valid) return { success: false, errors: prep.errors };
    if (prep.dryRun) return { success: true, dryRun: true };
    return executeAction(prep.commandId, async () => {
      const r = await api.delete(`/superadmin/functions/webhooks/${webhookId}`);
      await fetchWebhooks();
      return r.data;
    });
  };

  const pingWebhook = async (webhookId) => {
    const r = await api.post(`/superadmin/functions/webhooks/${webhookId}/ping`);
    await fetchWebhooks();
    return r.data;
  };

  const toggleWebhook = async (webhookId, active) => {
    const r = await api.patch(`/superadmin/functions/webhooks/${webhookId}`, { active });
    await fetchWebhooks();
    return r.data;
  };

  useEffect(() => { fetchAll(); }, []);

  return {
    routes, middlewares, jobs, jobHistory, webhooks, whHistory,
    loading, error,
    fetchAll, fetchRoutes, fetchJobs, fetchWebhooks,
    createJob, toggleJob, deleteJob, runJobNow,
    createWebhook, deleteWebhook, pingWebhook, toggleWebhook,
    dryRunMode,
  };
}

// ─── Onglet : Routes ──────────────────────────────────────────────────────

function OngletRoutes({ routes, middlewares, loading, onRefresh }) {
  const [search,    setSearch]    = useState('');
  const [filterMethod, setFilterMethod] = useState('ALL');
  const [showMiddlewares, setShowMiddlewares] = useState(false);

  const filtered = useMemo(() => {
    return routes.filter(r => {
      const matchSearch = !search || r.path.toLowerCase().includes(search.toLowerCase());
      const matchMethod = filterMethod === 'ALL' || r.methods.includes(filterMethod);
      return matchSearch && matchMethod;
    });
  }, [routes, search, filterMethod]);

  const methods = ['ALL', 'GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Filtrer par path..."
            className="w-full pl-8 pr-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-zinc-200 focus:border-orange-600/50 outline-none"
          />
        </div>
        <div className="flex gap-1">
          {methods.map(m => (
            <button key={m} onClick={() => setFilterMethod(m)}
              className={`px-2.5 py-1.5 rounded text-[10px] font-mono border transition-all ${
                filterMethod === m
                  ? 'bg-orange-950/40 border-orange-700/50 text-orange-300'
                  : 'bg-zinc-900 border-zinc-800 text-zinc-600 hover:text-zinc-400'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
        <button onClick={onRefresh} disabled={loading}
          className="p-2 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-500 hover:text-zinc-300 disabled:opacity-40">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <p className="text-[10px] text-zinc-700 uppercase tracking-widest">
        {filtered.length} route(s) affichée(s) sur {routes.length}
      </p>

      {/* Table routes */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl overflow-hidden">
        <div className="divide-y divide-zinc-800/40 max-h-[480px] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="py-10 text-center text-zinc-700 text-xs">Aucune route trouvée</div>
          ) : (
            filtered.map((route, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-800/20 group">
                <div className="flex gap-1 shrink-0">
                  {route.methods.map(m => <MethodBadge key={m} method={m} />)}
                </div>
                <span className="font-mono text-xs text-zinc-300 flex-1 truncate">{route.path}</span>
                {route.middlewares?.length > 0 && (
                  <div className="hidden group-hover:flex items-center gap-1 shrink-0">
                    {route.middlewares.slice(0, 3).map((mw, j) => (
                      <Badge key={j} color="purple" size="xs">{mw}</Badge>
                    ))}
                    {route.middlewares.length > 3 && (
                      <span className="text-[9px] text-zinc-700">+{route.middlewares.length - 3}</span>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Middlewares globaux */}
      <div>
        <button
          onClick={() => setShowMiddlewares(v => !v)}
          className="flex items-center gap-2 text-xs text-zinc-600 hover:text-zinc-400"
        >
          <Layers size={12} />
          Middlewares globaux ({middlewares.length})
          <ChevronDown size={12} className={`transition-transform ${showMiddlewares ? 'rotate-180' : ''}`} />
        </button>
        {showMiddlewares && (
          <div className="mt-2 space-y-1">
            {middlewares.map((mw, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-1.5 bg-zinc-900/30 rounded text-xs">
                <Badge color={mw.type === 'error-handler' ? 'rose' : 'sky'} size="xs">{mw.type}</Badge>
                <span className="font-mono text-zinc-400">{mw.name}</span>
                {mw.regexp && <span className="text-zinc-700 text-[10px] truncate">{mw.regexp}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
} 

// ─── Onglet : Jobs ────────────────────────────────────────────────────────

const CRON_PRESETS = [
  { label: 'Toutes les minutes',  value: '* * * * *'      },
  { label: 'Toutes les 5 min',    value: '*/5 * * * *'    },
  { label: 'Toutes les heures',   value: '0 * * * *'      },
  { label: 'Tous les jours à 2h', value: '0 2 * * *'      },
  { label: 'Tous les lundis',     value: '0 9 * * 1'      },
  { label: 'Personnalisé',        value: 'custom'         },
];

const JOB_TYPES = [
  { value: 'health-check',    label: 'Health Check',     desc: 'Vérifie la santé du système'        },
  { value: 'cache-flush',     label: 'Vider le cache',   desc: 'Purge le cache Redis learners'       },
  { value: 'backup',          label: 'Backup',           desc: 'Déclenche une sauvegarde'            },
  { value: 'log-rotate',      label: 'Rotation logs',    desc: 'Archive et purge les anciens logs'   },
  { value: 'custom-webhook',  label: 'Webhook custom',   desc: 'Appelle une URL externe'             },
];

function OngletJobs({ jobs, jobHistory, loading, onCreateJob, onToggleJob, onDeleteJob, onRunNow, dryRunMode }) {
  const [showForm,  setShowForm]  = useState(false);
  const [feedback,  setFeedback]  = useState(null);
  const [formData,  setFormData]  = useState({
    name: '', schedule: '*/5 * * * *', schedulePreset: '*/5 * * * *',
    type: 'health-check', description: '', webhookUrl: '',
  });

  const showFb = (type, msg) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), 4000);
  };

  const handleCreate = async () => {
    const payload = {
      name:        formData.name,
      schedule:    formData.schedule,
      type:        formData.type,
      description: formData.description,
      payload:     formData.type === 'custom-webhook' ? { url: formData.webhookUrl } : {},
    };
    const r = await onCreateJob(payload);
    if (r.dryRun)    showFb('dry-run', `Job "${formData.name}" simulé (non créé)`);
    else if (r.success) { showFb('success', `Job "${formData.name}" créé`); setShowForm(false); }
    else showFb('error', r.errors?.join(' ') || r.error);
  };

  return (
    <div className="space-y-4">
      <Feedback {...(feedback || {})} onClear={() => setFeedback(null)} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-zinc-600 uppercase tracking-widest">{jobs.length} job(s) actif(s)</p>
        <button onClick={() => setShowForm(v => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 border border-zinc-700 hover:border-orange-600/40 rounded text-zinc-300 text-xs">
          <Plus size={12} />
          Nouveau job
        </button>
      </div>

      {/* Formulaire création */}
      {showForm && (
        <div className="bg-zinc-900/50 border border-orange-800/30 rounded-xl p-5 space-y-4">
          <h4 className="text-sm font-black text-orange-300 uppercase tracking-tighter">
            {dryRunMode ? '🧪 ' : ''}Nouveau job cron
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Nom</label>
              <input value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))}
                className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none"
                placeholder="ex: daily-health-check" />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Type</label>
              <select value={formData.type} onChange={e => setFormData(p => ({...p, type: e.target.value}))}
                className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none">
                {JOB_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label} — {t.desc}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Schedule</label>
              <select
                value={formData.schedulePreset}
                onChange={e => {
                  const v = e.target.value;
                  setFormData(p => ({...p, schedulePreset: v, schedule: v === 'custom' ? '' : v}));
                }}
                className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none mb-1"
              >
                {CRON_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              {formData.schedulePreset === 'custom' && (
                <input value={formData.schedule}
                  onChange={e => setFormData(p => ({...p, schedule: e.target.value}))}
                  className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs font-mono text-zinc-200 focus:border-orange-600/50 outline-none"
                  placeholder="* * * * *" />
              )}
              <p className="text-[9px] text-zinc-700 mt-1 font-mono">{formData.schedule}</p>
            </div>
            {formData.type === 'custom-webhook' && (
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">URL Webhook</label>
                <input value={formData.webhookUrl}
                  onChange={e => setFormData(p => ({...p, webhookUrl: e.target.value}))}
                  className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none"
                  placeholder="https://..." />
              </div>
            )}
            <div className="sm:col-span-2">
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Description</label>
              <input value={formData.description}
                onChange={e => setFormData(p => ({...p, description: e.target.value}))}
                className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none"
                placeholder="Description optionnelle..." />
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-300 text-xs">Annuler</button>
            <button onClick={handleCreate} disabled={!formData.name || !formData.schedule}
              className="px-4 py-2 bg-orange-600/30 hover:bg-orange-600/50 border border-orange-500/40 rounded text-orange-200 text-xs font-medium disabled:opacity-30">
              {dryRunMode ? '🧪 Simuler' : 'Créer le job'}
            </button>
          </div>
        </div>
      )}

      {/* Liste jobs */}
      <div className="space-y-2">
        {jobs.length === 0 ? (
          <div className="text-center py-10 border border-zinc-800/40 rounded-xl text-zinc-700 text-sm">
            Aucun job enregistré
          </div>
        ) : (
          jobs.map(job => (
            <div key={job.id} className={`bg-zinc-900/40 border rounded-xl p-4 transition-all ${
              job.active ? 'border-zinc-800/60' : 'border-zinc-800/30 opacity-60'
            }`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${job.active ? 'bg-emerald-500 animate-pulse' : 'bg-zinc-600'}`} />
                    <span className="text-sm font-bold text-zinc-200">{job.name}</span>
                    <Badge color="sky" size="xs">{job.type}</Badge>
                    {job.lastStatus === 'failure' && <Badge color="rose" size="xs">dernier échec</Badge>}
                  </div>
                  <div className="flex items-center gap-4 text-[10px] text-zinc-600">
                    <span className="font-mono">{job.schedule}</span>
                    {job.lastRun && <span>Dernier: {new Date(job.lastRun).toLocaleTimeString('fr-FR')}</span>}
                    <span>{job.runCount || 0} exéc.</span>
                    {job.description && <span className="text-zinc-700 italic">{job.description}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => onRunNow(job.id)}
                    className="p-1.5 bg-zinc-800 hover:bg-emerald-950/40 border border-zinc-700 hover:border-emerald-700/40 rounded text-zinc-500 hover:text-emerald-400"
                    title="Lancer maintenant">
                    <Play size={12} />
                  </button>
                  <button onClick={() => onToggleJob(job.id)}
                    className={`p-1.5 border rounded ${
                      job.active
                        ? 'bg-amber-950/30 border-amber-700/30 text-amber-400 hover:bg-amber-950/50'
                        : 'bg-zinc-800 border-zinc-700 text-zinc-500 hover:text-emerald-400'
                    }`}
                    title={job.active ? 'Désactiver' : 'Activer'}>
                    {job.active ? <Pause size={12} /> : <Play size={12} />}
                  </button>
                  <button onClick={async () => {
                    if (!window.confirm(`Supprimer le job "${job.name}" ?`)) return;
                    const r = await onDeleteJob(job.id);
                    showFb(r.dryRun ? 'dry-run' : r.success ? 'success' : 'error',
                      r.dryRun ? 'DRY-RUN : suppression simulée' : r.success ? 'Job supprimé' : (r.errors?.join(' ') || r.error));
                  }}
                    className="p-1.5 bg-zinc-800 border border-zinc-700 hover:border-red-700/40 rounded text-zinc-600 hover:text-red-400">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Historique exécutions */}
      {jobHistory.length > 0 && (
        <div className="bg-zinc-900/30 border border-zinc-800/40 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 border-b border-zinc-800/40 text-[10px] uppercase tracking-widest text-zinc-600">
            Historique exécutions (dernières {jobHistory.length})
          </div>
          <div className="divide-y divide-zinc-800/30 max-h-48 overflow-y-auto">
            {jobHistory.map(e => (
              <div key={e.execId} className="flex items-center gap-3 px-4 py-2 text-[10px]">
                <span className="text-zinc-700 font-mono whitespace-nowrap">
                  {new Date(e.executedAt).toLocaleTimeString('fr-FR')}
                </span>
                <span className={`font-bold uppercase w-16 ${e.outcome === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>
                  {e.outcome}
                </span>
                <span className="text-zinc-500 truncate">{e.jobName}</span>
                <span className="text-zinc-700 ml-auto">{e.durationMs}ms</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Onglet : Webhooks ────────────────────────────────────────────────────

const WEBHOOK_EVENTS = [
  'learner.created','learner.deleted','cohorte.created','cohorte.deleted',
  'instance.suspended','service.restarted','backup.completed',
  'alert.critical','migration.deployed','migration.rolledback',
];

function OngletWebhooks({ webhooks, whHistory, onCreateWebhook, onDeleteWebhook, onPing, onToggle, dryRunMode }) {
  const [showForm,  setShowForm]  = useState(false);
  const [feedback,  setFeedback]  = useState(null);
  const [pingResult, setPingResult] = useState({});
  const [formData,  setFormData]  = useState({ name: '', url: '', events: [], description: '' });

  const showFb = (type, msg) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), 4000);
  };

  const toggleEvent = (ev) => {
    setFormData(p => ({
      ...p,
      events: p.events.includes(ev) ? p.events.filter(e => e !== ev) : [...p.events, ev],
    }));
  };

  const handleCreate = async () => {
    const r = await onCreateWebhook(formData);
    if (r.dryRun)     showFb('dry-run', `Webhook "${formData.name}" simulé (non créé)`);
    else if (r.success) { showFb('success', `Webhook créé`); setShowForm(false); setFormData({ name:'', url:'', events:[], description:'' }); }
    else showFb('error', r.errors?.join(' ') || r.error);
  };

  const handlePing = async (webhookId) => {
    setPingResult(p => ({...p, [webhookId]: { loading: true }}));
    const r = await onPing(webhookId);
    setPingResult(p => ({...p, [webhookId]: r}));
    setTimeout(() => setPingResult(p => { const n = {...p}; delete n[webhookId]; return n; }), 5000);
  };

  return (
    <div className="space-y-4">
      <Feedback {...(feedback || {})} onClear={() => setFeedback(null)} />

      <div className="flex items-center justify-between">
        <p className="text-[10px] text-zinc-600 uppercase tracking-widest">{webhooks.length} webhook(s)</p>
        <button onClick={() => setShowForm(v => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 border border-zinc-700 hover:border-orange-600/40 rounded text-zinc-300 text-xs">
          <Plus size={12} />
          Nouveau webhook
        </button>
      </div>

      {/* Formulaire */}
      {showForm && (
        <div className="bg-zinc-900/50 border border-orange-800/30 rounded-xl p-5 space-y-4">
          <h4 className="text-sm font-black text-orange-300 uppercase tracking-tighter">
            {dryRunMode ? '🧪 ' : ''}Nouveau webhook sortant
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Nom</label>
              <input value={formData.name} onChange={e => setFormData(p=>({...p,name:e.target.value}))}
                className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none"
                placeholder="ex: slack-alerts" />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">URL</label>
              <input value={formData.url} onChange={e => setFormData(p=>({...p,url:e.target.value}))}
                className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none"
                placeholder="https://hooks.slack.com/..." />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-2">Événements déclencheurs</label>
              <div className="flex flex-wrap gap-2">
                {WEBHOOK_EVENTS.map(ev => (
                  <button key={ev} onClick={() => toggleEvent(ev)}
                    className={`px-2.5 py-1 rounded border text-[10px] font-mono transition-all ${
                      formData.events.includes(ev)
                        ? 'bg-orange-950/40 border-orange-700/50 text-orange-300'
                        : 'bg-zinc-900 border-zinc-800 text-zinc-600 hover:text-zinc-400'
                    }`}
                  >
                    {ev}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-300 text-xs">Annuler</button>
            <button onClick={handleCreate} disabled={!formData.name || !formData.url}
              className="px-4 py-2 bg-orange-600/30 hover:bg-orange-600/50 border border-orange-500/40 rounded text-orange-200 text-xs font-medium disabled:opacity-30">
              {dryRunMode ? '🧪 Simuler' : 'Créer'}
            </button>
          </div>
        </div>
      )}

      {/* Liste webhooks */}
      <div className="space-y-2">
        {webhooks.length === 0 ? (
          <div className="text-center py-10 border border-zinc-800/40 rounded-xl text-zinc-700 text-sm">Aucun webhook</div>
        ) : (
          webhooks.map(wh => {
            const pr = pingResult[wh.id];
            return (
              <div key={wh.id} className={`bg-zinc-900/40 border rounded-xl p-4 ${wh.active ? 'border-zinc-800/60' : 'border-zinc-800/30 opacity-60'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${wh.active ? 'bg-emerald-500' : 'bg-zinc-600'}`} />
                      <span className="text-sm font-bold text-zinc-200">{wh.name}</span>
                      {wh.lastStatus && <StatusBadge status={wh.lastStatus} />}
                      {pr && !pr.loading && (
                        <Badge color={pr.success ? 'emerald' : 'rose'} size="xs">
                          ping {pr.success ? '✓' : '✗'} {pr.status || pr.error}
                        </Badge>
                      )}
                    </div>
                    <p className="text-[10px] text-zinc-600 font-mono truncate mb-1">{wh.url}</p>
                    <div className="flex flex-wrap gap-1">
                      {wh.events?.map(ev => <Badge key={ev} color="purple" size="xs">{ev}</Badge>)}
                    </div>
                    <div className="flex gap-4 mt-1.5 text-[10px] text-zinc-700">
                      <span>{wh.totalCalls || 0} appels</span>
                      {wh.failureCount > 0 && <span className="text-red-500">{wh.failureCount} échecs</span>}
                      {wh.lastCalledAt && <span>Dernier: {new Date(wh.lastCalledAt).toLocaleTimeString('fr-FR')}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => handlePing(wh.id)} disabled={pr?.loading}
                      className="p-1.5 bg-zinc-800 hover:bg-sky-950/40 border border-zinc-700 hover:border-sky-700/40 rounded text-zinc-500 hover:text-sky-400 disabled:opacity-40"
                      title="Ping test">
                      <Send size={12} />
                    </button>
                    <button onClick={() => onToggle(wh.id, !wh.active)}
                      className={`p-1.5 border rounded ${wh.active ? 'bg-amber-950/30 border-amber-700/30 text-amber-400' : 'bg-zinc-800 border-zinc-700 text-zinc-500'}`}
                      title={wh.active ? 'Désactiver' : 'Activer'}>
                      {wh.active ? <Pause size={12} /> : <Play size={12} />}
                    </button>
                    <button onClick={async () => {
                      if (!window.confirm(`Supprimer "${wh.name}" ?`)) return;
                      const r = await onDeleteWebhook(wh.id);
                      showFb(r.dryRun ? 'dry-run' : r.success ? 'success' : 'error',
                        r.dryRun ? 'DRY-RUN simulé' : r.success ? 'Webhook supprimé' : (r.errors?.join(' ') || r.error));
                    }}
                      className="p-1.5 bg-zinc-800 border border-zinc-700 hover:border-red-700/40 rounded text-zinc-600 hover:text-red-400">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Historique appels */}
      {whHistory.length > 0 && (
        <div className="bg-zinc-900/30 border border-zinc-800/40 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 border-b border-zinc-800/40 text-[10px] uppercase tracking-widest text-zinc-600">
            Historique appels
          </div>
          <div className="divide-y divide-zinc-800/30 max-h-40 overflow-y-auto">
            {whHistory.map((e, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2 text-[10px]">
                <span className="text-zinc-700 font-mono whitespace-nowrap">
                  {new Date(e.calledAt).toLocaleTimeString('fr-FR')}
                </span>
                <span className={`font-bold w-12 ${e.success ? 'text-emerald-400' : 'text-red-400'}`}>
                  {e.status || 'ERR'}
                </span>
                <Badge color="purple" size="xs">{e.event}</Badge>
                <span className="text-zinc-600 truncate">{e.webhookName}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Onglet : Testeur d'endpoint ──────────────────────────────────────────

function OngletTesteur() {
  const [method,   setMethod]   = useState('GET');
  const [path,     setPath]     = useState('');
  const [bodyRaw,  setBodyRaw]  = useState('');
  const [result,   setResult]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [parseErr, setParseErr] = useState(null);

  const handleTest = async () => {
    setParseErr(null);
    let body = undefined;

    if (bodyRaw.trim()) {
      try { body = JSON.parse(bodyRaw); }
      catch { setParseErr('Body JSON invalide'); return; }
    }

    setLoading(true);
    try {
      const r = await api.post('/superadmin/functions/test-endpoint', { method, path, body });
      setResult(r.data);
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.error || err.message });
    } finally {
      setLoading(false);
    }
  };

  const methods = ['GET','POST','PUT','PATCH','DELETE'];
  const statusColor = result?.response?.status
    ? result.response.status >= 500 ? 'text-red-400'
    : result.response.status >= 400 ? 'text-amber-400'
    : 'text-emerald-400'
    : 'text-zinc-600';

  return (
    <div className="space-y-4 max-w-3xl">
      <p className="text-[10px] text-zinc-600 uppercase tracking-widest">
        Testez n'importe quelle route interne — la requête est authentifiée avec le token superadmin
      </p>

      {/* Barre de requête */}
      <div className="flex gap-2">
        <div className="flex bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden shrink-0">
          {methods.map(m => (
            <button key={m} onClick={() => setMethod(m)}
              className={`px-3 py-2.5 text-xs font-mono border-r border-zinc-800 last:border-0 transition-all ${
                method === m ? 'bg-orange-950/40 text-orange-300' : 'text-zinc-600 hover:text-zinc-300'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
        <input value={path} onChange={e => setPath(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleTest()}
          className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-2.5 text-xs font-mono text-zinc-200 focus:border-orange-600/50 outline-none"
          placeholder="/admin/health/full" />
        <button onClick={handleTest} disabled={loading || !path.trim()}
          className="px-4 py-2.5 bg-orange-600/30 hover:bg-orange-600/50 border border-orange-500/40 rounded-lg text-orange-200 text-xs font-medium disabled:opacity-40 flex items-center gap-2 shrink-0">
          {loading ? <RefreshCw size={13} className="animate-spin" /> : <Send size={13} />}
          Envoyer
        </button>
      </div>

      {/* Body */}
      {['POST','PUT','PATCH'].includes(method) && (
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1.5">Body (JSON)</label>
          <textarea value={bodyRaw} onChange={e => setBodyRaw(e.target.value)}
            className="w-full h-28 bg-black border border-zinc-700 rounded-lg px-3 py-2.5 text-xs font-mono text-zinc-200 focus:border-orange-600/50 outline-none resize-none"
            placeholder='{ "key": "value" }' />
          {parseErr && <p className="text-red-400 text-[10px] mt-1">{parseErr}</p>}
        </div>
      )}

      {/* Résultat */}
      {result && (
        <div className="bg-black border border-zinc-800/60 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800/60 bg-zinc-900/40">
            <div className="flex items-center gap-3">
              <Terminal size={13} className="text-zinc-600" />
              <span className="text-xs text-zinc-400">Réponse</span>
              {result.response && (
                <>
                  <span className={`text-sm font-black ${statusColor}`}>{result.response.status}</span>
                  <span className="text-zinc-700 text-xs">{result.response.statusText}</span>
                  <span className="text-zinc-700 text-xs">{result.response.durationMs}ms</span>
                </>
              )}
              {result.error && <span className="text-red-400 text-xs">{result.error}</span>}
            </div>
            <button onClick={() => setResult(null)} className="text-zinc-600 hover:text-zinc-300">
              <X size={13} />
            </button>
          </div>
          {result.response?.data !== undefined && (
            <pre className="p-4 text-[10px] text-zinc-400 font-mono overflow-auto max-h-72 whitespace-pre-wrap">
              {JSON.stringify(result.response.data, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Panneau principal ────────────────────────────────────────────────────

const TABS = [
  { id: 'routes',   label: 'Routes',   icon: Globe   },
  { id: 'jobs',     label: 'Jobs',     icon: Clock   },
  { id: 'webhooks', label: 'Webhooks', icon: Webhook },
  { id: 'testeur',  label: 'Testeur',  icon: Terminal },
];

export default function PanneauFonctions() {
  const [activeTab, setActiveTab] = useState('routes');
  const data = useFunctionsData();

  return (
    <div className="space-y-5 max-w-6xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black text-orange-300 tracking-tighter">FONCTIONS & HOOKS</h2>
          <p className="text-[10px] text-zinc-600 uppercase tracking-widest mt-0.5">
            Routes · Jobs Cron · Webhooks · Testeur Endpoint
          </p>
        </div>
        <button onClick={data.fetchAll} disabled={data.loading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-400 hover:text-zinc-200 text-xs disabled:opacity-40">
          <RefreshCw size={12} className={data.loading ? 'animate-spin' : ''} />
          Rafraîchir
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-zinc-900/50 border border-zinc-800/60 rounded-xl p-1">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs transition-all ${
              activeTab === tab.id
                ? 'bg-orange-950/50 border border-orange-700/40 text-orange-300'
                : 'text-zinc-600 hover:text-zinc-300'
            }`}
          >
            <tab.icon size={13} />
            <span className="hidden sm:inline">{tab.label}</span>
            {tab.id === 'jobs'     && data.jobs.length     > 0 && <span className="text-[9px] bg-zinc-700 px-1.5 rounded-full">{data.jobs.length}</span>}
            {tab.id === 'webhooks' && data.webhooks.length > 0 && <span className="text-[9px] bg-zinc-700 px-1.5 rounded-full">{data.webhooks.length}</span>}
          </button>
        ))}
      </div>

      {/* Contenu */}
      {activeTab === 'routes'   && <OngletRoutes   routes={data.routes} middlewares={data.middlewares} loading={data.loading} onRefresh={data.fetchRoutes} />}
      {activeTab === 'jobs'     && <OngletJobs     jobs={data.jobs} jobHistory={data.jobHistory} loading={data.loading} onCreateJob={data.createJob} onToggleJob={data.toggleJob} onDeleteJob={data.deleteJob} onRunNow={data.runJobNow} dryRunMode={data.dryRunMode} />}
      {activeTab === 'webhooks' && <OngletWebhooks webhooks={data.webhooks} whHistory={data.whHistory} onCreateWebhook={data.createWebhook} onDeleteWebhook={data.deleteWebhook} onPing={data.pingWebhook} onToggle={data.toggleWebhook} dryRunMode={data.dryRunMode} />}
      {activeTab === 'testeur'  && <OngletTesteur />}
    </div>
  );
}
